import axios from "axios";
import { compressImage } from "@/lib/imageCompress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Session token is stored in localStorage and sent as Authorization: Bearer
// on every request. This is the primary transport for auth — the HttpOnly
// cookie is a secondary fallback for clients where it works. Mobile Safari /
// iOS Chrome drop SameSite=None cookies between requests (ITP), and this
// header-based path sidesteps all of those cookie quirks.
const TOKEN_KEY = "tickdoc_session_token";
export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setAuthToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable — nothing we can do */ }
};

export const api = axios.create({ baseURL: API, withCredentials: true });

// Attach Bearer token to every request if we have one
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${t}`;
  }
  return config;
});

// Global 401 interceptor: any stale session boots the user back to /login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      // Stale token → drop it so the next mount doesn't redirect-loop
      setAuthToken(null);
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export const fetchWatches = (params = {}) =>
  api.get("/watches", {
    params,
    // Repeat array-valued params (e.g. status=[a,b] -> ?status=a&status=b)
    // so FastAPI's List[str] query params parse correctly.
    paramsSerializer: (p) => {
      const parts = [];
      Object.entries(p || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) {
          v.forEach((x) => {
            if (x !== undefined && x !== null && x !== "") {
              parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(x)}`);
            }
          });
        } else {
          parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        }
      });
      return parts.join("&");
    },
  }).then((r) => r.data);
export const fetchWatch = (id) => api.get(`/watches/${id}`).then((r) => r.data);
export const createWatch = (data) => api.post("/watches", data).then((r) => r.data);
export const updateWatch = (id, data) => api.put(`/watches/${id}`, data).then((r) => r.data);
export const patchWatchMedia = (id, patch) => api.patch(`/watches/${id}/media`, patch).then((r) => r.data);
export const deleteWatch = (id) => api.delete(`/watches/${id}`).then((r) => r.data);
export const archiveWatch = (id) => api.post(`/watches/${id}/archive`).then((r) => r.data);
export const unarchiveWatch = (id) => api.post(`/watches/${id}/unarchive`).then((r) => r.data);
export const unlistWatch = (id) => api.post(`/watches/${id}/unlist`).then((r) => r.data);
export const makePublic = (id) => api.post(`/watches/${id}/public`).then((r) => r.data);
export const makeUnpublic = (id) => api.post(`/watches/${id}/unpublic`).then((r) => r.data);
export const fetchStats = () => api.get("/watches/stats").then((r) => r.data);
export const fetchAttention = () => api.get("/watches/attention").then((r) => r.data);
export const fetchListings = () => api.get("/watches/listings").then((r) => r.data);
export const fetchAnalytics = () => api.get("/watches/analytics").then((r) => r.data);
export const fetchValidation = () => api.get("/validation").then((r) => r.data);
export const fetchValidationManageKeys = () => api.get("/validation/manage/keys").then((r) => r.data);
export const fetchValidationManageKey = (key) => api.get(`/validation/manage/${encodeURIComponent(key)}`).then((r) => r.data);
export const fetchValidationManage = () => api.get("/validation/manage").then((r) => r.data);
export const hideValidationValue = (key, value) =>
  api.post(`/validation/${encodeURIComponent(key)}/hide`, { value: String(value) }).then((r) => r.data);
export const unhideValidationValue = (key, value) =>
  api.post(`/validation/${encodeURIComponent(key)}/unhide`, { value: String(value) }).then((r) => r.data);
export const addValidationValue = (key, value) =>
  api.post(`/validation/${encodeURIComponent(key)}`, { value: String(value) }).then((r) => r.data);
export const renameValidationValue = (key, oldValue, newValue) =>
  // Safe variant: old_value travels in the body so values containing
  // slashes (e.g. "(07) In process / Rework") aren't truncated by the
  // URL router.
  api.patch(`/validation/${encodeURIComponent(key)}`, {
    old_value: String(oldValue),
    value: String(newValue),
  }).then((r) => r.data);
export const deleteValidationValue = (key, value) =>
  api.post(`/validation/${encodeURIComponent(key)}/delete`, { value: String(value) }).then((r) => r.data);
export const standardizeMovementTypes = (from_value = "Auto", to_value = "Automatic") =>
  api.post(`/data/standardize-movement-types?from_value=${encodeURIComponent(from_value)}&to_value=${encodeURIComponent(to_value)}`).then((r) => r.data);
export const enrichMovementsFromWatches = (dryRun = false) =>
  api.post(`/movements/enrich-from-watches?dry_run=${dryRun}`).then((r) => r.data);
export const exportCsvUrl = () => `${API}/watches/export`;

// Stock parts catalog
export const fetchStockParts = () => api.get("/stock-parts").then((r) => r.data);
export const createStockPart = (data) => api.post("/stock-parts", data).then((r) => r.data);
export const updateStockPart = (id, data) => api.put(`/stock-parts/${id}`, data).then((r) => r.data);
export const deleteStockPart = (id) => api.delete(`/stock-parts/${id}`).then((r) => r.data);

export const fetchMovements = (params = {}) => api.get("/movements", { params }).then((r) => r.data);
export const fetchMovement = (id) => api.get(`/movements/${id}`).then((r) => r.data);
export const createMovement = (data) => api.post("/movements", data).then((r) => r.data);
export const updateMovement = (id, data) => api.put(`/movements/${id}`, data).then((r) => r.data);
export const deleteMovement = (id) => api.delete(`/movements/${id}`).then((r) => r.data);
export const lookupMovement = (caliber, mvmnt_mfg) =>
  api.get("/movements/lookup", { params: { caliber, mvmnt_mfg } }).then((r) => r.data);

export const enrichMovement = (caliber, mvmnt_mfg) =>
  api.post("/movements/enrich", { caliber, mvmnt_mfg }).then((r) => r.data);

export const enrichMovementsPreview = (limit = 50) =>
  api.post(`/movements/enrich-bulk/preview?limit=${limit}`).then((r) => r.data);

export const enrichMovementsApply = (items) =>
  api.post("/movements/enrich-bulk/apply", { items }).then((r) => r.data);

export const fetchOrphanCalibers = () =>
  api.get("/movements/orphan-calibers").then((r) => r.data);

export const createMissingMovements = (enrich = true, calibers = null) =>
  api.post("/movements/create-missing", { enrich, calibers }).then((r) => r.data);

// --- Document Library ---
// Universal reference store (PDFs, images, catalogs like BestFit, etc.).
// A single document may be linked to many calibers / watches / suppliers.
export const fetchLibrary = (params = {}) =>
  api.get("/library", {
    params,
    paramsSerializer: (p) => {
      const parts = [];
      Object.entries(p || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) {
          v.forEach((x) => {
            if (x !== undefined && x !== null && x !== "") {
              parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(x)}`);
            }
          });
        } else {
          parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        }
      });
      return parts.join("&");
    },
  }).then((r) => r.data);

export const fetchLibraryCategories = () =>
  api.get("/library/categories").then((r) => r.data);

export const fetchLibraryCollections = () =>
  api.get("/library/collections").then((r) => r.data);

export const createLibraryLink = (payload) =>
  api.post("/library/link", payload).then((r) => r.data);

export const fetchLibraryDoc = (id) =>
  api.get(`/library/${id}`).then((r) => r.data);

export const uploadLibraryDoc = async (file, meta = {}, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  // Lists travel as comma-separated strings; the backend splits them.
  const toStr = (v) => (Array.isArray(v) ? v.join(",") : v ?? "");
  ["title", "category", "manufacturer", "notes"].forEach((k) => {
    if (meta[k] !== undefined && meta[k] !== null) fd.append(k, String(meta[k]));
  });
  ["calibers", "watch_ids", "supplier_ids", "collections", "tags"].forEach((k) => {
    if (meta[k] !== undefined) fd.append(k, toStr(meta[k]));
  });
  const r = await api.post("/library", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    timeout: 5 * 60 * 1000,
  });
  return r.data;
};

export const updateLibraryDoc = (id, patch) =>
  api.patch(`/library/${id}`, patch).then((r) => r.data);

export const deleteLibraryDoc = (id) =>
  api.delete(`/library/${id}`).then((r) => r.data);

export const uploadMovementImage = async (file) => {
  const fd = new FormData();
  fd.append("file", await compressImage(file));
  const r = await api.post("/uploads/movement-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data; // { path }
};

export const uploadServicePhoto = async (file) => {
  const fd = new FormData();
  fd.append("file", await compressImage(file));
  const r = await api.post("/uploads/service-photo", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 2 * 60 * 1000,
  });
  return r.data; // { path }
};

export const fetchCaliberPhotos = (watchId, exclude) =>
  api.get("/services/caliber-photos", { params: { watch_id: watchId, exclude } }).then((r) => r.data);

export const setServicePhotos = (id, photos) =>
  api.patch(`/services/${id}/photos`, { photos }).then((r) => r.data);

export const uploadWatchMedia = async (slot, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", slot === "movement" ? file : await compressImage(file));
  const r = await api.post(`/uploads/watch-media/${slot}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    // 100 MB video uploads can run long on slow connections
    timeout: 5 * 60 * 1000,
  });
  return r.data; // { path, slot }
};

/**
 * Build a publicly-renderable URL for a stored file.
 *
 * <img>/<video> tags can't carry the Authorization header, and on iPad
 * Chrome / iOS Safari third-party cookies are blocked — so the auth token
 * travels as a `_token` query param. The middleware honours this only on
 * the /api/files/* route so it's a narrow exposure (the same token already
 * flows as a Bearer header from this client).
 *
 * For documents that you want to OPEN in a new tab (PDF, etc.), use
 * `openProtectedFile()` instead — that path fetches the bytes via the
 * authed axios client and returns a blob URL.
 */
export const fileUrl = (storagePath, width) => {
  if (!storagePath) return null;
  const t = getAuthToken();
  const params = new URLSearchParams();
  if (width) params.set("w", String(width));
  if (t) params.set("_token", t);
  const qs = params.toString();
  return `${API}/files/${storagePath}${qs ? `?${qs}` : ""}`;
};

/**
 * Open a protected file in a new tab.
 *
 * Plain anchor links to /api/files/... break when the frontend and backend
 * live on different origins: the browser can't attach the Bearer token or
 * a third-party cookie. We fetch the bytes through the authed axios client,
 * wrap them in a blob URL, and navigate the pre-opened tab to it.
 *
 * The new window is opened synchronously inside the click handler so mobile
 * popup blockers (esp. iOS Safari / iPad Chrome) don't suppress it.
 */
export const openProtectedFile = async (storagePath, filename) => {
  if (!storagePath) return;
  const win = typeof window !== "undefined" ? window.open("", "_blank") : null;
  try {
    const r = await api.get(`/files/${storagePath}`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    if (win && !win.closed) {
      win.location.href = blobUrl;
    } else {
      // Popup blocked — fall back to a tap-to-download link.
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoke after the tab has had time to render.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
};

export const movementsCsvUrl = () => `${API}/movements/export/csv`;
export const servicesCsvUrl = () => `${API}/services/export/csv`;
export const partsCsvUrl = () => `${API}/parts/export/csv`;

// Services
export const fetchServices = (params = {}) => api.get("/services", { params }).then((r) => r.data);
export const createService = (data) => api.post("/services", data).then((r) => r.data);
export const updateService = (id, data) => api.put(`/services/${id}`, data).then((r) => r.data);
export const deleteService = (id) => api.delete(`/services/${id}`).then((r) => r.data);
export const startService = (id) => api.post(`/services/${id}/start`).then((r) => r.data);
export const completeService = (id, body = {}) => api.post(`/services/${id}/complete`, body).then((r) => r.data);

// Parts
export const fetchParts = (params = {}) => api.get("/parts", { params }).then((r) => r.data);
export const fetchPartStatuses = () => api.get("/parts/statuses").then((r) => r.data);
export const createPart = (data) => api.post("/parts", data).then((r) => r.data);
export const updatePart = (id, data) => api.put(`/parts/${id}`, data).then((r) => r.data);
export const deletePart = (id) => api.delete(`/parts/${id}`).then((r) => r.data);

// Suppliers
export const fetchSuppliers = (params = {}) => api.get("/suppliers", { params }).then((r) => r.data);
export const createSupplier = (data) => api.post("/suppliers", data).then((r) => r.data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data).then((r) => r.data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`).then((r) => r.data);
export const enrichSupplier = (url) => api.post("/suppliers/enrich", { url }).then((r) => r.data);


// ── Inspection ───────────────────────────────────────────────────────────────
export const fetchInspectionReference = () =>
  api.get("/inspection/reference").then((r) => r.data);
export const createInspectionCategory = (data) =>
  api.post("/inspection/categories", data).then((r) => r.data);
export const updateInspectionCategory = (id, data) =>
  api.patch(`/inspection/categories/${id}`, data).then((r) => r.data);
export const deleteInspectionCategory = (id) =>
  api.delete(`/inspection/categories/${id}`).then((r) => r.data);
export const createInspectionOption = (catId, data) =>
  api.post(`/inspection/categories/${catId}/options`, data).then((r) => r.data);
export const updateInspectionOption = (id, data) =>
  api.patch(`/inspection/options/${id}`, data).then((r) => r.data);
export const deleteInspectionOption = (id) =>
  api.delete(`/inspection/options/${id}`).then((r) => r.data);
export const fetchWatchInspection = (watchId) =>
  api.get(`/watches/${watchId}/inspection`).then((r) => r.data);
export const saveWatchInspection = (watchId, data) =>
  api.put(`/watches/${watchId}/inspection`, data).then((r) => r.data);
